import { HomeOutlined, ReloadOutlined } from "@ant-design/icons"
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd"
import { useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import type {
  CatalogFilters,
  CatalogTemplate,
  PointRevision,
  RegulatoryScope,
} from "../../../types/regulatory"
import { scopeLabels } from "../../../utils/regulatory"
import { RegulatoryApiError } from "../../../utils/regulatoryApi"
import "../regulatory.css"

import { textValue } from "../format"
export function CatalogFrame({
  title,
  children,
  extra,
}: {
  title: string
  children: ReactNode
  extra?: ReactNode
}) {
  return (
    <section className="regulatory-page">
      <Breadcrumb
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: <Link to="/regulatory/home">Réglementaire</Link> },
          { title },
        ]}
      />
      <div className="regulatory-heading">
        <Typography.Title level={2} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
        <Space wrap>{extra}</Space>
      </div>
      {children}
    </section>
  )
}
export function ResourceState({
  loading,
  error,
  retry,
  children,
}: {
  loading: boolean
  error: Error | null
  retry: () => void
  children: ReactNode
}) {
  if (loading)
    return (
      <div className="regulatory-loading">
        <Spin size="large" tip="Chargement du catalogue…">
          <div style={{ height: 70 }} />
        </Spin>
      </div>
    )
  if (error)
    return (
      <Alert
        type="error"
        showIcon
        message={
          error instanceof RegulatoryApiError && error.status === 409
            ? "Définition non résolue ou conflit de révision"
            : "Chargement impossible"
        }
        description={
          <Space direction="vertical">
            <span>{error.message}</span>
            <Button onClick={retry} icon={<ReloadOutlined />}>
              Réessayer
            </Button>
          </Space>
        }
      />
    )
  return <>{children}</>
}
export function ScopeTag({ scope }: { scope: RegulatoryScope }) {
  return (
    <Tag color={scope === "site" ? "blue" : scope === "common" ? "cyan" : "purple"}>
      {scopeLabels[scope]}
    </Tag>
  )
}
export function ReviewTag({ validated }: { validated: boolean }) {
  return (
    <Tag color={validated ? "green" : "orange"}>{validated ? "Revu en interne" : "Non revu"}</Tag>
  )
}
export function OwnershipTag({ template }: { template: CatalogTemplate }) {
  return <Tag>{template.enterpriseUuid ? "Entreprise" : "Catalogue partagé"}</Tag>
}
export function RequirementTag({ value }: { value: boolean | undefined }) {
  return (
    <Tag color={value === true ? "blue" : undefined}>
      {value === undefined ? "Non renseigné" : value ? "Requis" : "Non requis"}
    </Tag>
  )
}
export function NatureTag({ point }: { point: PointRevision["definition"] }) {
  return (
    <Tag
      color={
        point.coverageGap || point.interpretationReserve
          ? "orange"
          : point.nature === "Recommandation"
            ? "blue"
            : undefined
      }
    >
      {textValue(point.nature)}
    </Tag>
  )
}
export function SearchInput({
  value,
  onSearch,
  placeholder = "Rechercher dans le catalogue",
}: {
  value: string
  onSearch: (value: string) => void
  placeholder?: string
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <Input.Search
      aria-label={placeholder}
      placeholder={placeholder}
      value={draft}
      maxLength={200}
      allowClear
      enterButton="Rechercher"
      onChange={(event) => {
        setDraft(event.target.value)
        if (!event.target.value) onSearch("")
      }}
      onSearch={onSearch}
      style={{ width: 360, maxWidth: "100%" }}
    />
  )
}
export function CatalogFiltersBar({
  filters,
  update,
  children,
}: {
  filters: CatalogFilters
  update: (patch: Record<string, string | boolean | undefined>) => void
  children?: ReactNode
}) {
  return (
    <div className="regulatory-toolbar">
      <SearchInput value={filters.q ?? ""} onSearch={(value) => update({ q: value })} />
      <Select
        aria-label="Périmètre"
        placeholder="Tous les périmètres"
        allowClear
        value={filters.scope}
        onChange={(value) => update({ scope: value })}
        options={Object.entries(scopeLabels).map(([value, label]) => ({ value, label }))}
        style={{ minWidth: 210 }}
      />
      <Select
        aria-label="Revue interne"
        placeholder="Toutes les revues"
        allowClear
        value={filters.validated === undefined ? undefined : String(filters.validated)}
        onChange={(value) => update({ validated: value })}
        options={[
          { value: "true", label: "Revu en interne" },
          { value: "false", label: "Non revu" },
        ]}
        style={{ minWidth: 170 }}
      />
      <Select
        aria-label="État d’archivage"
        value={filters.archived ? "true" : "false"}
        onChange={(value) => update({ archived: value })}
        options={[
          { value: "false", label: "Actifs" },
          { value: "true", label: "Archivés" },
        ]}
        style={{ minWidth: 125 }}
      />
      {children}
    </div>
  )
}
export function PointDetails({ point }: { point: PointRevision }) {
  const p = point.definition
  return (
    <div className="regulatory-point-detail">
      <Space wrap>
        <NatureTag point={p} />
        {p.coverageGap && <Tag color="orange">Complément absent</Tag>}
        {p.interpretationReserve && <Tag color="orange">Réserve d’interprétation</Tag>}
      </Space>
      <Descriptions
        size="small"
        column={{ xs: 1, md: 2 }}
        bordered
        items={[
          { key: "code", label: "Code de contrôle", children: textValue(p.controlCode) },
          { key: "section", label: "Section", children: textValue(p.section) },
          { key: "reference", label: "Référence", children: textValue(p.reference), span: 2 },
          {
            key: "application",
            label: "Applicabilité",
            children: <span className="regulatory-text">{textValue(p.applicability)}</span>,
            span: 2,
          },
          {
            key: "method",
            label: "Méthode",
            children: <span className="regulatory-text">{textValue(p.method)}</span>,
            span: 2,
          },
          {
            key: "expected",
            label: "Résultat attendu",
            children: <span className="regulatory-text">{textValue(p.expected)}</span>,
            span: 2,
          },
          {
            key: "unit",
            label: "Unité / description de mesure",
            children: textValue(p.unit),
            span: 2,
          },
          { key: "photo", label: "Photo", children: <RequirementTag value={p.photoRequired} /> },
          {
            key: "measure",
            label: "Mesure",
            children: <RequirementTag value={p.measurementRequired} />,
          },
          {
            key: "evidence",
            label: "Justificatif",
            children: <RequirementTag value={p.evidenceRequired} />,
          },
          {
            key: "note",
            label: "Note d’interprétation",
            children: <span className="regulatory-text">{textValue(p.interpretationNote)}</span>,
            span: 2,
          },
          {
            key: "document",
            label: "Document source",
            children: textValue(p.sourceDocument),
            span: 2,
          },
          {
            key: "clause",
            label: "Clause",
            children: textValue(p.sourceClause ?? point.source?.clause),
          },
          {
            key: "pages",
            label: "Pages source",
            children: textValue(p.sourcePages ?? point.source?.pageLabel),
          },
          {
            key: "pdfpages",
            label: "Pages PDF",
            children: (p.sourcePdfPages ?? point.source?.pdfPages)?.join(", ") || "Non renseigné",
          },
          { key: "part", label: "Partie source", children: p.sourcePart ?? "Non renseigné" },
          { key: "filename", label: "Fichier source", children: textValue(p.sourceFile), span: 2 },
          {
            key: "pack",
            label: "Paquet source",
            children: p.sourcePack ? (
              <Link to={`/regulatory/sources?tab=packs&q=${encodeURIComponent(p.sourcePack)}`}>
                {p.sourcePack}
              </Link>
            ) : (
              "Non renseigné"
            ),
            span: 2,
          },
          {
            key: "related",
            label: "Références associées",
            children: p.relatedReferences?.length
              ? p.relatedReferences.join(" ; ")
              : "Non renseigné",
            span: 2,
          },
          {
            key: "sourceLink",
            label: "Provenance",
            children: (
              <Space wrap>
                {point.source?.referenceEditionUuid && (
                  <Link
                    to={`/regulatory/references?q=${encodeURIComponent(point.source.referenceEditionUuid)}`}
                  >
                    Édition de référence
                  </Link>
                )}
                {point.source?.documentUuid && (
                  <Link
                    to={`/regulatory/sources?q=${encodeURIComponent(point.source.documentUuid)}`}
                  >
                    Document source
                  </Link>
                )}
              </Space>
            ),
            span: 2,
          },
        ]}
      />
      {p.missingPart && (
        <Alert showIcon type="warning" message={`Complément absent : ${p.missingPart}`} />
      )}
    </div>
  )
}
export function JsonDetails({ value }: { value: unknown }) {
  return <pre className="regulatory-json">{JSON.stringify(value, null, 2)}</pre>
}
export function EmptyCatalog() {
  return (
    <Card>
      <Empty description="Aucune définition ne correspond aux filtres." />
    </Card>
  )
}
